import { action, computed, makeObservable, observable, observableRef, toJS } from 'mobx';
import { MunsellMetricModel } from '@cutwise/color-visualizer/lib/color/entities/MunsellMetric';
import { isArray } from '@cutwise/utils';

import { REFERENCE_IDS } from '@/common/constants/references';
import { assignModelField } from '@/common/helpers/assignModel';
import { CustomGradeCode } from '@/customGrading/constants/customGradeCodes';
import { GradeDictEntryModel } from '@/customGrading/entity/GradeDictEntry';
import GradeResult from '@/customGrading/entity/GradeResult';
import { PinnedProduct } from '@/forwarding/entities/PinnedItem/PinnedProduct';
import { createEmptyForm } from '@/forwarding/types/ForwardForm';
import { ForwardInfo } from '@/forwarding/types/ForwardInfo';
import Project from '@/hpo/entity/Project';
import Solution from '@/hpo/entity/Solution';
import MediaCollection from '@/media/entity/MediaCollection';
import { MediaSubFile } from '@/media/entity/MediaSubFile';
import { SetupPresetType } from '@/media/entity/SetupPreset';
import { BrightnessMetricModel } from '@/metric/entity/BrightnessMetric';
import { ColorMetricModel } from '@/metric/entity/ColorMetric';
import { DeadZoneMetricModel } from '@/metric/entity/DeadZoneMetric';
import { FireMetricModel } from '@/metric/entity/FireMetric';
import { FluorMetricModel } from '@/metric/entity/FluorMetric';
import { IntegralMetricModel } from '@/metric/entity/IntegralMetric';
import { IntegralV2MetricModel } from '@/metric/entity/IntegralV2Metric';
import { PavillionColorViboxMetricModel } from '@/metric/entity/PavillionColorViboxMetric';
import { ScintillationMetricModel } from '@/metric/entity/ScintillationMetric';
import { SpreadMetricModel } from '@/metric/entity/SpreadMetric';
import { SymmetryMetricModel } from '@/metric/entity/SymmetryMetric';
import { DriveProjectSummary } from '@/multistage/entities/DriveProjectSummary';
import { CommentModel } from '@/product/entity/CommentModel';
import { ExpensesParams } from '@/product/entity/Expenses/Expenses.types';
import { ReportDataModel } from '@/product/entity/ReportData';
import { ReportImageModel } from '@/product/entity/ReportImage';
import { Contact } from '@/security/entities/Contact';
import { GlobalAccessTarget } from '@/security/entities/GlobalAccessTarget';
import { ContactAccessInfo } from '@/security/types/ContactAccessInfo';
import { UserV2Model } from '@/user/entity/UserV2';

import {
  COMPARE_WITH_REFERENCE_PRODUCT_TYPES,
  ProductType,
  TYPE_DIAMOND_COLORLESS,
  TYPE_DIAMOND_FANCY,
  TYPE_JEWELRY,
  TYPE_LGD_COLORLESS,
  TYPE_LGD_FANCY,
  TYPE_LGD_ROUGH,
  TYPE_ROUGH,
} from '../constants/productTypes';
import { formDiff } from '../helpers/formDiff';

import CutShape from './CutShape';
import Expenses from './Expenses/Expenses';

export const READABLE_TYPES = {
  [TYPE_DIAMOND_COLORLESS]: 'Loose Diamond',
  [TYPE_DIAMOND_FANCY]: 'Loose Colored Diamond',
  [TYPE_LGD_COLORLESS]: 'Lab-Grown Diamond',
  [TYPE_LGD_FANCY]: 'Lab-Grown Colored Diamond',
  [TYPE_LGD_ROUGH]: 'Lab-Grown Rough Diamond',
  [TYPE_JEWELRY]: 'Jewelry Item',
  [TYPE_ROUGH]: 'Rough Diamond',
  [ProductType.Gem]: 'Gem',
};

export type ProductId = Brand<number, 'ProductId'>;

export interface ProductB2bModel {
  id: number;
  title: string;
  user?: UserV2Model;
}

export interface ProductCustomGradeModel {
  grade: string;
  title: string;
}

export interface FancyDispersionModel extends ProductCustomGradeModel {
  modelScore: number;
  uniformityGrade: number;
  uniformityScore: number;
  position: number;
}

export interface HueScoreModel extends MunsellMetricModel {
  grade?: string;
  title?: string;
  gradeDistributionTitle?: string;
  hueName?: string;
}

export interface ProductScoresModel {
  hue: Nullable<HueScoreModel>;
  fire: FireMetricModel;
  color: Nullable<ColorMetricModel>;
  fluor: Nullable<FluorMetricModel>;
  spread: SpreadMetricModel;
  integral: IntegralMetricModel;
  integralV2: IntegralV2MetricModel;
  integralNorm: IntegralMetricModel;
  symmetry: SymmetryMetricModel;
  deadzones: DeadZoneMetricModel;
  brilliance: BrightnessMetricModel;
  pavcolorvibox: Nullable<PavillionColorViboxMetricModel>;
  scintillation: ScintillationMetricModel;
  fancyDispersion?: FancyDispersionModel;
}

export interface AbsorptionSpectrumModel {
  source: string;
  type: string;
  fileUrl: string;
  dzLiteId: Nullable<number>;
}

export interface ProductEmbedModel {
  shortView?: string;
  b2b?: ProductB2bModel;
  reportData?: ReportDataModel;
  reportImg?: ReportImageModel[];
  i3dReport?: string;
  i3dmini?: string;
  i3dcomp?: string;
  htmlReport?: string;
  i3dcompReport?: string;
  ox2zScene?: string;
  dmc?: string;
  cbr?: string;
  acl?: ContactAccessInfo[];
  expenses?: ExpensesParams;
  driveProject?: DriveProjectSummary;
  customGrades?: Record<CustomGradeCode, ProductCustomGradeModel>;
  scores?: ProductScoresModel;
  absorptionSpectra?: AbsorptionSpectrumModel[];
}

export interface ProductLinkModel {
  href: string;
  rel: 'project.view' | 'solution.view';
  id: string;
  title: string;
}

export class ProductModel {
  id!: ProductId;
  createdAt!: string;
  updatedAt!: string;
  b2bSid!: string;
  price!: Nullable<number>;
  carat!: number;
  access!: number;
  dataAccess!: number;
  cutShape!: Nullable<CutShape>;
  manufacturer!: Nullable<string>;
  seller!: ProductB2bModel;
  sellerNote!: Nullable<string>;
  // TODO add model
  mediaCollection!: MediaCollection;
  _originalCarat!: number;
  isLabGrown!: boolean;
  hpoGuid!: Nullable<string>;
  comment?: CommentModel;
  altSku!: Nullable<string>;
  _entityType?: ProductType;
  _embedded?: ProductEmbedModel;
  _links?: ProductLinkModel[];

  // not used, but required in ".populate()"
  fire?: never;
}

export default class Product extends ProductModel {
  createdAt: string = '';

  b2bSid: string = '';

  price: Nullable<number> = null;

  declare carat: number;

  access: number = 0;

  dataAccess: number = 0;

  cutShape: Nullable<CutShape> = null;

  manufacturer: Nullable<string> = null;

  declare seller: ProductB2bModel;

  // Real B2B data (can be different to 'seller' field). Visible only to ADMIN role
  b2b: Nullable<ProductB2bModel> = null;

  sellerNote: Nullable<string> = null;

  declare mediaCollection: MediaCollection;

  declare _originalCarat: number;

  declare isLabGrown: boolean;

  hpoGuid: Nullable<string> = null;

  declare comment?: CommentModel;

  altSku: Nullable<string> = null;

  acl: ContactAccessInfo[] = [];

  i3dmini: string = '';

  i3dcomp: string = '';

  ox2zScene: string = '';

  shortView: string = '';

  customGrades: Partial<Record<CustomGradeCode, GradeDictEntryModel | GradeResult>> = {};

  project?: Project;

  projectLink?: ProductLinkModel;

  solution?: Solution;

  solutionLink?: ProductLinkModel;

  driveProject: Nullable<DriveProjectSummary> = null;

  cbr: string = '';

  expenses?: Expenses;

  entityType!: ProductType;

  constructor(modelData: ProductModel) {
    super();

    makeObservable(this, {
      hpoGuid: false,
      priceCt: false,
      toForwardInfo: false,
      toPinnedItem: false,
      toGlobalAccessTarget: false,
      diff: false,
      getMediaBySetupPreset: false,
      toJS: false,
      createdAt: observable,
      b2bSid: observable,
      price: observable,
      carat: observable,
      access: observable,
      dataAccess: observable,
      cutShape: observable,
      manufacturer: observable,
      seller: observable,
      b2b: observableRef,
      sellerNote: observable,
      mediaCollection: observable,
      _originalCarat: observable,
      isLabGrown: observable,
      comment: observableRef,
      altSku: observableRef,
      acl: observable,
      i3dmini: observable,
      i3dcomp: observable,
      ox2zScene: observable,
      shortView: observable,
      customGrades: observable,
      project: observableRef,
      projectLink: observableRef,
      solution: observableRef,
      solutionLink: observableRef,
      driveProject: observableRef,
      cbr: observableRef,
      expenses: observable,
      entityType: observable,
      isLoaded: computed,
      hasMedia: computed,
      defaultMedia: computed,
      type: computed,
      sellerId: computed,
      sellerEmail: computed,
      isValid: computed,
      isReference: computed,
      isComparableWithReference: computed,
      setProject: action,
      setSolution: action,
      populate: action,
    });

    this.populate(modelData);
  }

  get priceCt() {
    let val = 0.0;

    if (this.price && this.carat) {
      val = Math.round((this.price / this.carat) * 100) / 100;
    }

    return val;
  }

  set priceCt(val) {
    if (!this.carat) {
      return;
    }

    this.price = Math.round(val * this.carat * 100) / 100;
  }

  get isLoaded() {
    return Boolean(this.b2bSid);
  }

  get hasMedia() {
    return this.mediaCollection && this.mediaCollection.mediaItems && this.mediaCollection.mediaItems.length > 0;
  }

  get defaultMedia() {
    if (!this.hasMedia) {
      return null;
    }

    return this.mediaCollection.mediaItems[0];
  }

  get type(): ProductType {
    return this.entityType;
  }

  toForwardInfo(): ForwardInfo {
    return {
      preview: {
        type: 'productEntity',
        product: this,
      },
      ...createEmptyForm(),
      title: [this.b2bSid, this.shortView].filter(Boolean).join(' - '),
    };
  }

  toPinnedItem(): PinnedProduct {
    return new PinnedProduct(this);
  }

  toGlobalAccessTarget(): GlobalAccessTarget {
    return new GlobalAccessTarget({
      id: this.id,
      access: this.access,
      dataAccess: this.dataAccess,
      hasDataAccess: false,
    });
  }

  get sellerId() {
    if (!this.seller) {
      return null;
    }

    return this.seller.id;
  }

  get sellerEmail() {
    return this.seller && this.seller.user && this.seller.user.email ? this.seller.user.email : null;
  }

  get isValid() {
    return true;
  }

  get isReference() {
    return REFERENCE_IDS.includes(this.id);
  }

  get isComparableWithReference() {
    return COMPARE_WITH_REFERENCE_PRODUCT_TYPES.some((type) => type === this.type) && !this.isReference;
  }

  diff(product: Product) {
    return formDiff(product, this);
  }

  setProject(project: Project) {
    this.project = project;

    return this;
  }

  setSolution(solution: Solution) {
    this.solution = solution;

    return this;
  }

  populate(modelData: ProductModel) {
    if (modelData.fire) {
      delete modelData.fire;
    }

    (Object.keys(modelData) as (keyof ProductModel)[]).forEach((propertyKey) => {
      if (typeof modelData[propertyKey] === 'function') {
        return;
      }

      if (propertyKey === 'mediaCollection' && !(modelData[propertyKey] instanceof MediaCollection)) {
        return;
      }

      assignModelField<ProductModel>(this, modelData, propertyKey);
    });

    if (this._embedded && this._embedded.shortView) {
      this.shortView = this._embedded.shortView;
    }

    if (this._embedded && this._embedded.b2b) {
      this.b2b = this._embedded.b2b;
    }

    if (this._embedded && this._embedded.i3dmini) {
      this.i3dmini = this._embedded.i3dmini;
    }

    if (this._embedded && this._embedded.i3dcomp) {
      this.i3dcomp = this._embedded.i3dcomp;
    }

    if (this._embedded && this._embedded.ox2zScene) {
      this.ox2zScene = this._embedded.ox2zScene;
    }

    if (this._embedded && Array.isArray(this._embedded.acl)) {
      this.acl = this._embedded.acl.map((acl) => ({ ...acl, assignee: new Contact(acl.assignee) }));
    }

    if (this._embedded && this._embedded.driveProject) {
      this.driveProject = this._embedded.driveProject;
    }

    if (this._embedded && this._embedded.cbr) {
      this.cbr = this._embedded.cbr;
    }

    delete this._entityType;

    if (!this.entityType) {
      this.entityType = modelData._entityType!;
    }

    if (modelData.mediaCollection && !(modelData.mediaCollection instanceof MediaCollection)) {
      this.mediaCollection = new MediaCollection(modelData.mediaCollection, this);
    }

    if (this._embedded && this._embedded.customGrades) {
      this.customGrades = {};

      (Object.keys(this._embedded.customGrades) as CustomGradeCode[]).forEach((customGradeCode) => {
        this.customGrades[customGradeCode] = new GradeResult({
          value: this._embedded!.customGrades![customGradeCode].grade,
          customGradeCode: customGradeCode,
        });
      });
    }

    if (this._embedded && this._embedded.expenses) {
      this.expenses = new Expenses(this._embedded.expenses);
    }

    if (isArray(this._links)) {
      this._links.forEach((link) => {
        switch (link.rel) {
          case 'project.view':
            this.projectLink = link;

            break;
          case 'solution.view':
            this.solutionLink = link;

            break;
          default:
            break;
        }
      });
    }

    if (modelData.hpoGuid) {
      this.hpoGuid = modelData.hpoGuid;
    }

    if (this.comment) {
      this.comment = modelData.comment;
    }

    this.altSku = modelData.altSku;

    if (modelData.cutShape) {
      this.cutShape = new CutShape(modelData.cutShape);
    }
  }

  getMediaBySetupPreset(sp: Nullable<SetupPresetType> | undefined, size?: number): Nullable<MediaSubFile> {
    if (!this.mediaCollection) {
      return null;
    }

    const mediaCollection = this.mediaCollection.items.find((media) => media.sp === sp);

    if (!mediaCollection || mediaCollection.isEmpty) {
      return null;
    }

    let media: Nullable<MediaSubFile> = null;

    mediaCollection.mediaSources.forEach((source) => {
      if (typeof size === 'undefined') {
        media = source.mediaSubFiles[0];

        return;
      }

      source.mediaSubFiles.forEach((subFile) => {
        if (subFile.resolutionX === size) {
          media = subFile;
        }
      });
    });

    return media;
  }

  toJS() {
    return toJS({ ...this });
  }
}

export function addCollectionCommentsToProducts(products: Product[], collectionProducts: Product[]) {
  const commentMap = new Map(collectionProducts.map((p) => [p.id, p.comment]));

  return products.map((p) => {
    p.comment = commentMap.get(p.id);

    return p;
  });
}
